import {animate, style, transition, trigger} from '@angular/animations';
import {ChangeDetectionStrategy, Component, computed, effect, inject, input, linkedSignal, output, signal} from '@angular/core';
import {DOCUMENT} from '@angular/common';
import {TranslateModule} from '@ngx-translate/core';

import {TooltipDirective} from 'src/app/modules/design-system/tooltip/tooltip.component';

// The rail is a top-down chain of 45px buttons with an 18px gap, so every item
// sits exactly 63px below the one above it. Starting an item there — scaled down
// and behind its predecessor (see .rail-item z-index) — is what makes the button
// read as sliding out of the button above, i.e. out of the always-present close
// button for the first one to appear, and back into it on the way out. Keep in
// sync with the rail's SCSS geometry.
const RAIL_SLOT_TRAVEL = 'translateY(-63px) scale(0.4)';

// Time the rail takes to come out from under the drawer, its wait for the drawer
// and the stagger of the last button included. Mirrors $rail-slide-in-total.
const RAIL_SLIDE_IN_MS = 760;

/** Which conditional buttons the rail is showing. */
interface RailButtons {
	/** Back to the drawer underneath (drawer history). */
	back: boolean;
	comment: boolean;
	/** Back from a full-panel view of the current drawer (settings, comments) to its tabs. */
	backView: boolean;
}

const NO_BUTTONS: RailButtons = {back: false, comment: false, backView: false};

/**
 * Floating action rail of the right drawer, hanging off its left edge.
 *
 * Presentational: every piece of state it shows is an input and every interaction
 * is an output, which is what lets it be OnPush. The services behind those values
 * (RightSliderService, ApplicationUrlService) expose plain getters with no change
 * notification, so reading them from here would let the rail go stale — the smart
 * container polls them and pushes the values down instead.
 *
 * The one thing it owns is the choreography of its own entrance and exit: which
 * buttons are on screen while it slides (`buttons`) and how a button appears
 * (`slotMode`). Both are timing, not state — see below.
 */
@Component({
	selector: 'app-right-drawer-rail',
	standalone: true,
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [TranslateModule, TooltipDirective],
	templateUrl: './right-drawer-rail.component.html',
	styleUrl: './right-drawer-rail.component.scss',
	// On the host rather than the rail: the clip that hides the rail behind the
	// drawer has to sit on an element the slide doesn't move (see the SCSS).
	// `rail-landed` marks the end of the entrance, which the SCSS needs too — a
	// button showing up after it belongs to a rail that is already out.
	host: {
		'[class.rail-opened]': 'opened()',
		'[class.rail-landed]': 'landed()',
	},
	animations: [
		trigger('railSlot', [
			// 'chain' — the rail is already out and a button becomes available on its
			// own, so it slides down out of the button above it. Slight overshoot on
			// the way in so it reads as ejected rather than merely translated; the way
			// out is quicker and plain ease-in.
			transition('void => chain', [
				style({opacity: 0, transform: RAIL_SLOT_TRAVEL}),
				animate('260ms cubic-bezier(0.34, 1.4, 0.64, 1)',
					style({opacity: 1, transform: 'translateY(0) scale(1)'})),
			]),
			transition('chain => void', [
				animate('180ms ease-in',
					style({opacity: 0, transform: RAIL_SLOT_TRAVEL})),
			]),
			// 'ride' — the rail itself is travelling out from under the drawer and
			// carries the button along with it. No transition is declared for it on
			// purpose: a vertical entrance of its own would fight that slide.
		]),
	],
})
export class RightDrawerRailComponent {

	readonly opened = input<boolean>(false);
	readonly canGoBack = input<boolean>(false);
	readonly canComment = input<boolean>(false);
	readonly canGoBackView = input<boolean>(false);
	readonly unreadComments = input<number>(0);
	readonly commentsCount = input<number>(0);

	readonly closed = output<void>();
	readonly wentBack = output<void>();
	readonly wentBackView = output<void>();
	readonly commentsToggled = output<void>();

	/**
	 * The buttons to render — the live inputs while the rail is open, the last
	 * open values once it isn't.
	 *
	 * Closing the drawer clears its history in the very tick it clears `opened`,
	 * so without the freeze the conditional buttons would blink out of existence
	 * instead of riding back under the drawer. They stay rendered afterwards,
	 * which costs nothing: the closed rail is clipped away entirely.
	 */
	private readonly buttons = linkedSignal<RailButtons & {opened: boolean}, RailButtons>({
		source: () => ({
			opened: this.opened(),
			back: this.canGoBack(),
			comment: this.canComment(),
			backView: this.canGoBackView(),
		}),
		computation: (source, previous) => source.opened
			? {back: source.back, comment: source.comment, backView: source.backView}
			: previous?.value ?? NO_BUTTONS,
	});

	protected readonly showBack = computed(() => this.buttons().back || this.buttons().backView);
	/** A full-panel view wins over the drawer history: leave the view first, the drawer next. */
	protected readonly showBackView = computed(() => this.buttons().backView);
	protected readonly showComment = computed(() => this.buttons().comment);

	/** Sous 960px le rail vit dans l'en-tête du drawer (home.component.scss) : l'en-tête réserve la place de ses boutons via cette variable (styles.scss). */
	private readonly document = inject(DOCUMENT);
	private readonly publishCount = effect(() => {
		const count = 1 + (this.showBack() ? 1 : 0) + (this.showComment() ? 1 : 0);
		this.document.documentElement.style.setProperty('--drawer-rail-count', String(count));
	});

	/** True once the rail has finished coming out from under the drawer. */
	protected readonly landed = signal(false);

	/** Entry style of a conditional button — see the `railSlot` trigger. */
	protected readonly slotMode = computed(() => this.landed() ? 'chain' : 'ride');

	constructor() {
		// `landed` is deliberately false for the whole opening sequence, and reset
		// the moment the rail closes: a button that shows up in that window is one
		// the rail is bringing out itself, not one appearing on an idle rail.
		effect(onCleanup => {
			if (!this.opened()) {
				this.landed.set(false);
				return;
			}
			const timer = setTimeout(() => this.landed.set(true), RAIL_SLIDE_IN_MS);
			onCleanup(() => clearTimeout(timer));
		});
	}
}
